// Shared loan-report engine — TanStack Query hooks for the per-discipline
// monthly report ledger. Mirrors the usePortfolioAdmin conventions:
//   • Stable query-key factory.
//   • Mutation hooks invalidate the touched query on success.
//   • Toasts + error rendering happen in the calling component.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { loanReportsApi } from "@/api/portfolioAdmin/loanReportsApi";
import type {
  LoanReportDiscipline,
  LoanReportDto,
} from "@/api/portfolioAdmin/loanReportsApi";
import { invalidateDmsDocumentScope, invalidateLoanHealth } from "./invalidation";

export function loanReportsKey(loanId: string, discipline: LoanReportDiscipline) {
  return ["portfolio-admin", "loan", loanId, "reports", discipline] as const;
}

export function useLoanReports(
  loanId: string | undefined,
  discipline: LoanReportDiscipline,
) {
  return useQuery<LoanReportDto[]>({
    queryKey: loanReportsKey(loanId ?? "", discipline),
    queryFn: () => loanReportsApi.list(loanId!, discipline),
    enabled: !!loanId,
  });
}

export function useGenerateLoanReport(
  loanId: string,
  discipline: LoanReportDiscipline,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { periodStart: string; periodEnd: string }) =>
      loanReportsApi.generate(loanId, { discipline, ...body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanReportsKey(loanId, discipline) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useCertifyLoanReport(
  loanId: string,
  discipline: LoanReportDiscipline,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) => loanReportsApi.certify(reportId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanReportsKey(loanId, discipline) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useFileLoanReport(
  loanId: string,
  discipline: LoanReportDiscipline,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) => loanReportsApi.file(reportId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: loanReportsKey(loanId, discipline) });
      invalidateLoanHealth(qc, loanId);
      // Filing uploads the PDF into the loan's DMS folder.
      void invalidateDmsDocumentScope(qc);
    },
  });
}
